// 261010 Epic 2 / P3d — CHÍNH SÁCH phiếu "AI chấm" bước Đạo diễn & diễn xuất / Quay phim (thuần: không antd/router, jest chạy được).
// Quyết: chip kết luận · nút chấm / chấm lại bật khi nào · phân xử (ghi chú bắt buộc theo VERDICT, không theo số finding) · gom finding theo shot · tiền · lỗi API ⇒ chữ.
// Kết quả chỉ HIỆN + ghi sổ (Phase doc QĐ phạm vi): không đổi `state` bước, không chặn máy chạy ⇒ không có nút "AI tự sửa" ở đây.
// Hợp đồng dữ liệu = response THẬT của BE (`_p3d/dev-be-1.md` §8): `reviews[]` mỗi (video, bước) có `latest` (phiếu DONE hoặc null) · `running` (phiếu đang chạy MỚI HƠN, `overdue` do BE tính) · `previous[]`.
import type { FilmRunningJob } from '../../types/film';
import type {
  ClipSnapshotMeta, MediaEvidence, LeaderDecision, PromptPick, PromptSnapshotMeta, ReviewFinding, ReviewSnapshotMeta, StepReview, StepReviewBudget, StepReviewBudgetScope, StepReviewItem, StepReviewKey, StepReviewsResponse, StepReviewVerdict,
} from '../../types/filmStepReview';
import { coverageLine, coverageSummary, frameLabel } from './reviewMediaPolicy';
import { isRunActive } from './styleProfilePolicy';
import { workflowActionError } from './workflowActionPolicy';

export const REVIEW_JOB_TYPE = 'FILM_STEP_REVIEW';
/** `MAX_MINUTES` của job chấm (QĐ 8) — chỉ để nói trong câu chữ; việc "quá hạn" do BE quyết (`running.overdue`). */
export const REVIEW_MAX_MINUTES = 15;
/** QĐ 4: trích dẫn dài hơn ⇒ cắt khi hiển thị. */
export const EVIDENCE_MAX = 400;

export const REVIEW_TITLE: Record<StepReviewKey, string> = {
  DIRECTING: 'AI chấm đạo diễn & diễn xuất · gồm tuân thủ luật phong cách',
  CINEMATOGRAPHY: 'Duyệt A · AI chấm quay phim, nhịp dựng và luật phong cách',
  STORYBOARD_REVIEW: 'Duyệt B · AI xem ảnh thật',
  PROMPT_REVIEW: 'AI chấm prompt', // đúng mẫu reviewTop key 6 (mockup chốt)
  CLIP_REVIEW: 'AI chấm clip', // đúng mẫu reviewTop key 7; thẻ ghép thêm " · Bản N" (clipReviewTitle)
};

export const isReviewStep = (step: string): step is StepReviewKey => step === 'DIRECTING' || step === 'CINEMATOGRAPHY';
/**
 * Bước UI (WorkflowStepKey) → khoá phiếu. P5c: bước Storyboard dùng phiếu Duyệt B (`STORYBOARD_REVIEW`); P5d: bước Prompt dùng `PROMPT_REVIEW`;
 * Đạo diễn / Quay phim như P3d; P6: bước Video dùng `CLIP_REVIEW` (mỗi bản clip một phiếu); bước khác không có phiếu. Tách khỏi `isReviewStep` (giữ nguyên nghĩa "bước 3, 4 của P3d").
 */
export function reviewKeyOfStep(step: string): StepReviewKey | null {
  if (isReviewStep(step)) return step;
  if (step === 'STORYBOARD') return SB_REVIEW;
  if (step === 'PROMPT') return PR_REVIEW;
  return step === 'VIDEO' ? CL_REVIEW : null;
}
export const SB_REVIEW: StepReviewKey = 'STORYBOARD_REVIEW';
export const PR_REVIEW: StepReviewKey = 'PROMPT_REVIEW';
export const CL_REVIEW: StepReviewKey = 'CLIP_REVIEW';
const isSb = (k: StepReviewKey | undefined): boolean => k === SB_REVIEW;
const isPr = (k: StepReviewKey | undefined): boolean => k === PR_REVIEW;
const isCl = (k: StepReviewKey | undefined): boolean => k === CL_REVIEW;
/** P6: V5 "dùng được trong phim" luôn chỉ là GỢI Ý (đo P0 4/10, P0b 5/7; Gemini từng nhầm camera tiến vào thành vật biến mất) — kể cả khi BE gửi BLOCKING. */
export const isAdvisoryOnlyCriterion = (stepKey: StepReviewKey | undefined, criterion: string): boolean => isCl(stepKey) && criterion === 'V5';

/** Chữ DỰ PHÒNG của tiêu chí (QĐ 3) khi BE chưa trả `criteria`. Chữ của BE được ưu tiên. */
const CRITERION_LABEL: Record<string, string> = {
  D1: 'Nhịp quan trọng có kích thích → phản ứng → trạng thái sau nhìn thấy được',
  D2: 'Diễn xuất cụ thể bằng cơ thể / mặt / hành động',
  D3: 'Hành động / phép có cơ chế tác động đọc được',
  D4: 'Áp luật hồ sơ phong cách',
  D5: 'Shot rỗng hoặc lặp ý',
  D6: 'Mục đích shot còn mờ',
  C1: 'Máy phục vụ cue diễn xuất',
  C2: 'Hướng mặt / trục / liên kết nhịp hợp lý với kịch bản',
  C3: 'Không dùng kiểu quay "kịch tính" vô cớ',
  C4: 'Áp luật hồ sơ phong cách',
  C5: 'Nhịp dựng so với dải của hồ sơ',
  C6: 'Câu camera rườm hoặc mâu thuẫn',
  // P5c — Duyệt B (rubric `film_step_review_storyboard_v1`)
  B1: 'Đúng hành động / bố cục theo shot',
  B2: 'Nền, hướng máy và hướng chủ thể khớp view đã gửi',
  B3: 'Đúng người / trang phục; không nhân đôi hay vật lạ trong cùng frame',
  B4: 'Cue diễn xuất nhìn thấy được',
  B5: 'Dễ đọc và liên tục (chỉ để tham khảo)',
  // P5d — AI chấm prompt (rubric `film_step_review_prompt_v1`)
  P1: 'Câu camera & chuyển động phục vụ đúng cue, không rườm hay cụt',
  P2: 'Đúng nhân vật, hành động, thoại so với kịch bản / storyboard',
  P3: 'Token @ImageN khớp ảnh đính (thứ tự, vai trò, không bỏ sót ref)',
  P4: 'Không có từ làm hỏng hình hoặc vượt giới hạn engine',
  P5: 'Nhịp ngắt câu tự nhiên (chỉ để tham khảo)',
  // P6 — AI chấm clip (rubric `film_step_review_clip_v1`)
  V1: 'Đúng hành động chính; hướng nhìn / trục máy khớp bối cảnh đã định',
  V2: 'Đúng người / trang phục; không nhân đôi nhân vật, không vật lạ thò vào',
  V3: 'Diễn xuất và chuyển động tự nhiên; không biến dạng, không nhấp nháy',
  V4: 'Âm thanh / thoại đúng lúc, khớp kịch bản; không câm, không vỡ tiếng',
  V5: 'Dùng được trong phim — nhịp, cảm xúc, thẩm mỹ (chỉ để tham khảo)',
};
export type Criteria = Record<string, string> | undefined;
/** Mã lạ không có chữ ở cả BE lẫn dự phòng ⇒ giữ nguyên mã (không bịa chữ). */
export function criterionText(code: string, criteria?: Criteria): string {
  const label = criteria?.[code] || CRITERION_LABEL[code];
  return label ? `${code} · ${label}` : code;
}

/** P6: `CLIP_REVIEW` có NHIỀU phiếu mỗi video (mỗi bản clip một phiếu) ⇒ không tra theo (video, bước) mà theo `clipItemOf` (clipReviewPolicy) — ở đây luôn null để khỏi lấy nhầm phiếu của bản khác. */
export const itemOf = (resp: StepReviewsResponse | null | undefined, eventId: string, stepKey: StepReviewKey): StepReviewItem | null =>
  (isCl(stepKey) ? null : resp?.reviews?.find((i) => i.eventId === eventId && i.stepKey === stepKey) ?? null);

// ───────────────────────── pha + poll ─────────────────────────

export type ReviewPhase = 'NONE' | 'RUNNING' | 'OVERDUE' | 'DONE';
/** RUNNING = có phiếu đang chạy còn trong hạn (kể cả khi `latest` là phiếu cũ); OVERDUE = phiếu đang chạy quá hạn (BE báo); DONE = chỉ có phiếu đã xong. */
export function reviewPhase(item: StepReviewItem | null): ReviewPhase {
  if (!item) return 'NONE';
  if (item.running) return item.running.overdue ? 'OVERDUE' : 'RUNNING';
  return item.latest ? 'DONE' : 'NONE';
}

/** Còn mục đang chạy trong hạn ⇒ poll. Quá hạn thì job đã chết / bị bỏ ⇒ KHÔNG poll mãi. */
export const shouldPollReviews = (resp: StepReviewsResponse | null | undefined): boolean =>
  !!resp?.reviews?.some((i) => reviewPhase(i) === 'RUNNING');

// ───────────────────────── chip + nút ─────────────────────────

export interface ReviewChip { tone: 'ok' | 'bad' | 'warn' | 'muted'; label: string }
const VERDICT_CHIP: Record<StepReviewVerdict, ReviewChip> = {
  PASS: { tone: 'ok', label: 'Đạt' },
  FIX_REQUIRED: { tone: 'bad', label: 'AI đồng ý có lỗi cần sửa' },
  NEEDS_LEADER: { tone: 'warn', label: 'Hai AI không thống nhất — cần bạn xem' },
  INCOMPLETE: { tone: 'muted', label: 'Chấm dở' },
};
/** Chip của phiếu DONE mới nhất (độc lập với việc đang chạy lượt mới). */
export const latestChip = (item: StepReviewItem | null): ReviewChip | null => (item?.latest ? VERDICT_CHIP[item.latest.verdict] ?? VERDICT_CHIP.INCOMPLETE : null);
/** Chip trạng thái chung: đang chạy / quá hạn thắng, không thì chip kết luận. */
export function verdictChip(item: StepReviewItem | null): ReviewChip | null {
  const phase = reviewPhase(item);
  if (phase === 'NONE') return null;
  if (phase === 'RUNNING') return { tone: 'muted', label: 'Đang chấm…' };
  if (phase === 'OVERDUE') return { tone: 'warn', label: 'Chấm dở (quá hạn)' };
  return latestChip(item);
}
export const staleNote = (item: StepReviewItem | null): string | null => (item?.stale && item.latest ? 'Phiếu cũ — dữ liệu đã đổi' : null);

export interface RunButton { label: string; enabled: boolean; reason?: string }
export function runButton(
  item: StepReviewItem | null,
  o: { busy: boolean; runActive: boolean; jobs?: Array<Pick<FilmRunningJob, 'type' | 'key' | 'eventId'>>; eventId: string; stepKey: StepReviewKey; candidateClipId?: string | null },
): RunButton {
  const phase = reviewPhase(item);
  const label = item?.latest || phase === 'OVERDUE' ? 'Chấm lại' : isSb(o.stepKey) ? 'AI chấm ảnh' : isPr(o.stepKey) ? 'AI chấm prompt' : isCl(o.stepKey) ? 'Chấm clip này' : 'AI chấm bước này';
  const off = (reason: string): RunButton => ({ label, enabled: false, reason });
  // Duyệt B: BE tải + chuẩn hoá tới 12 ảnh TRONG request POST trước khi trả 202 ⇒ có thể chậm vài–vài chục giây; nút khoá tới lúc có 202 / 409.
  // P6: POST chấm clip cũng tải video + ffmpeg trích khung hình / âm thanh TRƯỚC khi giữ chỗ ⇒ vài chục giây; nút khoá tới lúc có 202 / 409.
  if (o.busy) return off(isSb(o.stepKey) ? 'Đang chuẩn bị ảnh gửi giám khảo — có thể mất vài chục giây, đừng bấm lại.' : isCl(o.stepKey) ? 'Đang tải clip và trích khung hình — có thể mất vài chục giây, đừng bấm lại.' : 'Đang gửi — đợi một chút.');
  if (phase === 'RUNNING') return off('2 giám khảo đang chấm — đợi xong.');
  // P6: job chấm clip khoá theo TỪNG bản (`review:<clipId>:CLIP_REVIEW`) — hai bản chấm song song được, bản A đang chấm không khoá bản B.
  const ownsJob = (j: Pick<FilmRunningJob, 'key'>): boolean => !j.key || (j.key.endsWith(`:${o.stepKey}`) && (!isCl(o.stepKey) || !o.candidateClipId || j.key.includes(o.candidateClipId)));
  if (o.jobs?.some((j) => j.type === REVIEW_JOB_TYPE && j.eventId === o.eventId && ownsJob(j))) return off(isCl(o.stepKey) ? 'Bản clip này đang được chấm ở nền — đợi xong.' : 'Bước này đang được chấm ở nền — đợi xong.');
  if (o.runActive) return off('Máy tự chạy chặng đang chạy trên plan này — đợi xong rồi chấm.');
  return { label, enabled: true };
}
export { isRunActive };

// ───────────────────────── tiền + model viết ─────────────────────────

/** P5c QĐ 4: tiền cộng theo plan + scope. Đạo diễn + Quay phim chung `P3D`; Duyệt B là `P5C`; AI chấm prompt là `P5D` (mỗi ví một trần riêng). */
export const budgetScopeOf = (stepKey: StepReviewKey): StepReviewBudgetScope => (isSb(stepKey) ? 'P5C' : isPr(stepKey) ? 'P5D' : isCl(stepKey) ? 'P6' : 'P3D');
/**
 * Số tiền ĐÚNG ví của bước. BE: `budgets[scope]` mỗi ví một số; `budget` LUÔN là ví P3D (BE cũ chưa có `budgets` ⇒ chỉ còn `budget`).
 * Duyệt B KHÔNG BAO GIỜ mượn số P3D (sẽ in sai trần); không có ví P5C ⇒ undefined ⇒ dòng tiền không hardcode.
 */
export function budgetFor(resp: StepReviewsResponse | null | undefined, stepKey: StepReviewKey): StepReviewBudget | undefined {
  if (!resp) return undefined;
  const scope = budgetScopeOf(stepKey);
  return resp.budgets?.[scope] ?? (scope === 'P3D' ? resp.budget : undefined);
}
/** Số tiền cấp PLAN (BE: `usedUsd` = DONE cost + RUNNING reserve của cả plan/scope). Thiếu / hỏng ⇒ chỉ nói "2 giám khảo AI", không hardcode trần. */
export function budgetLine(b: StepReviewBudget | undefined, stepKey?: StepReviewKey): string {
  if (isSb(stepKey)) {
    if (!b || !Number.isFinite(b.usedUsd) || !Number.isFinite(b.capUsd)) return '2 giám khảo AI xem ảnh';
    return `2 giám khảo AI xem ảnh · đã dùng + giữ chỗ $${b.usedUsd.toFixed(2)} / trần chấm ảnh $${b.capUsd.toFixed(2)} của plan`;
  }
  if (isCl(stepKey)) {
    if (!b || !Number.isFinite(b.usedUsd) || !Number.isFinite(b.capUsd)) return '2 giám khảo AI xem clip';
    return `2 giám khảo AI xem clip · đã dùng + giữ chỗ $${b.usedUsd.toFixed(2)} / trần chấm clip $${b.capUsd.toFixed(2)} của plan`;
  }
  if (isPr(stepKey)) {
    if (!b || !Number.isFinite(b.usedUsd) || !Number.isFinite(b.capUsd)) return '2 giám khảo AI soát prompt';
    return `2 giám khảo AI soát prompt · đã dùng + giữ chỗ $${b.usedUsd.toFixed(2)} / trần soát prompt $${b.capUsd.toFixed(2)} của plan`;
  }
  if (!b || !Number.isFinite(b.usedUsd) || !Number.isFinite(b.capUsd)) return '2 giám khảo AI';
  return `2 giám khảo AI · đã dùng $${b.usedUsd.toFixed(2)} / trần $${b.capUsd.toFixed(2)} của tập`;
}
export function reviewCostLine(r: StepReview): string {
  if (r.costUsd === null || r.costUsd === undefined || !Number.isFinite(r.costUsd)) return 'Tiền phiếu: chưa có số';
  const m = `$${r.costUsd.toFixed(4)}`;
  return r.costComplete ? `Tiền phiếu: ${m} (đo được)` : `Tiền phiếu: ${m} (chưa đủ số đo — phần thiếu tính theo mức dự trữ)`;
}
/** QĐ 1b: model viết LUÔN là giả định theo cấu hình lúc chấm. */
export const writerLine = (r: StepReview): string | null => (r.writerModel ? `Model viết (giả định theo cấu hình): ${r.writerModel}` : null);

// ───────────────────────── phân xử ─────────────────────────

export interface DecisionGate { canDecide: boolean; noteRequired: boolean; reason?: string }
const DECISION_LABEL: Record<LeaderDecision, string> = { ACCEPT: 'Chấp nhận kết luận', NEEDS_FIX: 'Cần sửa' };
/** Phân xử nhắm vào `latest` (phiếu DONE). BE không chặn khi lượt MỚI quá hạn, nên OVERDUE vẫn cho phân xử phiếu DONE trước đó; đang chạy lượt mới trong hạn thì đợi. */
export function decisionGate(item: StepReviewItem | null): DecisionGate {
  const no = (reason: string): DecisionGate => ({ canDecide: false, noteRequired: false, reason });
  const r = item?.latest;
  if (!item) return no('Chưa có phiếu để phân xử.');
  if (reviewPhase(item) === 'RUNNING') return no('2 giám khảo đang chấm — đợi xong rồi phân xử.');
  if (!r) return no('Lượt chấm dở (quá hạn), chưa có phiếu nào xong — bấm Chấm lại.');
  if (r.leader) return no('Phiếu này đã được phân xử.');
  if (item.stale) return no('Phiếu cũ — dữ liệu đã đổi sau lúc chấm. Bấm Chấm lại rồi mới phân xử.');
  if (r.verdict === 'INCOMPLETE') return no('Phiếu chấm dở — không có kết luận để phân xử. Bấm Chấm lại.');
  return { canDecide: true, noteRequired: r.verdict === 'NEEDS_LEADER' || r.verdict === 'FIX_REQUIRED' };
}
export function validateDecision(item: StepReviewItem, decision: LeaderDecision, note: string): string | null {
  void decision; // ghi chú bắt buộc theo VERDICT, cho cả hai quyết định
  const g = decisionGate(item);
  if (!g.canDecide) return g.reason ?? 'Chưa phân xử được phiếu này.';
  if (g.noteRequired && !note.trim()) return 'Phiếu có lỗi — ghi chú lý do trước khi phân xử.';
  return null;
}
export interface DecisionLine { who: string; at: string; label: string; note: string }
export function decisionLine(r: StepReview): DecisionLine | null {
  if (!r.leader) return null;
  return { who: r.leader.decidedBy || '—', at: r.leader.decidedAt ?? '', label: DECISION_LABEL[r.leader.decision], note: r.leader.note ?? '' };
}

// ───────────────────────── finding ─────────────────────────

export interface FindingView {
  code: string; criterion: string;
  /** Nặng VÀ trích dẫn khớp — finding `unverified` không tính vào kết luận nên không đếm nặng. */
  blocking: boolean; sevLabel: string; shotOrder: number; shotLabel: string;
  evidence: string; problem: string;
  /** P5d: câu AI trích nguyên văn (`textEvidence`, rơi về `evidence`), ĐÃ trim, KHÔNG cắt 400 — để định vị trong khung prompt. */
  quote: string;
  /** Lời nhận định: 1 dòng với finding của một giám khảo; 2 dòng [A, B] với lỗi cả hai cùng thấy. */
  problems: string[]; suggestion: string; unverified: boolean;
  /** P5c: frame trong sheet (0 = không rõ / cả sheet) + bằng chứng hình thô. */
  frameNo: number; frameLabel: string; media: MediaEvidence[];
  /** P5c: lý do BE đánh `unverified` (rỗng nếu không có). */
  unverifiedReason: string;
  /** P6: mốc giây trong clip (null = BE không nêu / số hỏng) + nhãn "t = 8.5 s". */
  timestampS: number | null; timeLabel: string;
}
/** P5c: dòng cố định ở đầu thẻ Duyệt B — nói rõ phiếu KHÔNG chặn gì (User 25/10: "Chỉ hiện phiếu, không chặn"). */
export const STORYBOARD_REVIEW_NOTE = 'Phiếu chỉ để xem — không chặn Duyệt storyboard, dựng prompt hay tạo video.';
/** P5d: cùng lời hứa cho bước Prompt (User 25/10: "Chỉ hiện phiếu, không chặn"). */
export const PROMPT_REVIEW_NOTE = 'Phiếu chỉ để xem — không chặn dựng lại prompt, gen lại hay tạo video.';
/** P6: cùng lời hứa cho bước Video (User 25/10: "Chỉ hiện phiếu, không chặn"; Leader Rev 1: chọn bản có lỗi nặng chỉ hỏi lý do, không chặn cứng). */
export const CLIP_REVIEW_NOTE = 'Phiếu chỉ để xem — không chặn chọn bản, ghép tập hay tạo video.';
const NON_BLOCKING_CHIP = 'Mức lỗi nặng — phiếu không chặn';
/** Chip phụ cho Duyệt B / AI chấm prompt / AI chấm clip khi có lỗi nặng HỢP LỆ (agreed, hoặc finding BLOCKING chưa `unverified`; V5 không tính); "nặng" chỉ là mức trên phiếu, không khoá hành động. Bước P3D không có chip này. */
export function nonBlockingChip(stepKey: StepReviewKey, r: StepReview | null): string | null {
  if (!(isSb(stepKey) || isPr(stepKey) || isCl(stepKey)) || !r) return null;
  const heavy = (criterion: string): boolean => !isAdvisoryOnlyCriterion(stepKey, criterion);
  const hasBlocking = (r.agreed ?? []).some((a) => heavy(a.criterion)) || (r.judges ?? []).some((j) => (j.findings ?? []).some((f) => f.severity === 'BLOCKING' && !f.unverified && heavy(f.criterion)));
  return hasBlocking ? NON_BLOCKING_CHIP : null;
}
/** Giải thích finding mờ. Duyệt B: `unverified` = ĐỊA CHỈ bằng chứng hình sai (ảnh / frame / khoảng giây / vùng), không phải "AI nhìn sai". */
export const unverifiedNote = (stepKey: StepReviewKey, reason?: string): string => {
  const why = reason?.trim() ? ` Lý do: ${reason.trim()}.` : '';
  // P5d: câu trích phải khớp NGUYÊN VĂN đề / bản prompt gửi engine; không khớp ⇒ mờ, không tính vào kết luận, không cho bấm "xem trong prompt".
  if (isPr(stepKey)) return `Câu trích không khớp nguyên văn đề / bản prompt gửi engine — chưa tính vào kết luận.${why}`;
  // P6: mốc giây phải nằm trong clip và khung hình (nếu nêu) phải thuộc bộ khung đã trích; không thì BE đánh `unverified`.
  if (isCl(stepKey)) return `Mốc giây hoặc khung hình AI nêu không khớp bản clip đã chấm (ngoài độ dài clip / không thuộc bộ khung đã trích) — chưa tính vào kết luận.${why}`;
  if (!isSb(stepKey)) return 'Trích dẫn không khớp dữ liệu — chưa tính vào kết luận.';
  return `Địa chỉ bằng chứng hình không hợp lệ (ảnh / frame / khoảng giây / vùng không khớp bản đã gửi) — chưa tính vào kết luận.${why}`;
};
const cut = (s: string, n: number): string => (s.length > n ? `${s.slice(0, n)}…` : s);
const shotOf = (n: number): number => (Number.isFinite(n) && n >= 1 ? Math.floor(n) : 0);
const posInt = (n: unknown): number => (typeof n === 'number' && Number.isInteger(n) && n >= 1 ? n : 0);
/**
 * Frame của finding. BE phiếu HÌNH luôn gửi `frameNo` (0 = cấp cả tấm, dù có bằng chứng ở khung nào) ⇒ `frameNo` là số thì LÀ chân lý (số hỏng ⇒ 0 "Cả sheet");
 * chỉ khi KHÔNG có trường `frameNo` mới lấy khung của bằng chứng hình đầu tiên.
 */
const frameOf = (f: Pick<ReviewFinding, 'frameNo' | 'mediaEvidence'>): number => (typeof f.frameNo === 'number' ? posInt(f.frameNo) : posInt(f.mediaEvidence?.[0]?.frameNo));
const NO_TIME = 'Không rõ mốc giây';
/** Mốc giây hợp lệ: số hữu hạn ≥ 0 (BE đã kiểm nằm trong clip; ở đây chỉ chặn số hỏng). */
export const timeOf = (n: unknown): number | null => (typeof n === 'number' && Number.isFinite(n) && n >= 0 ? n : null);
export const timeLabelOf = (t: number | null): string => (t === null ? NO_TIME : `t = ${+t.toFixed(2)} s`);
/** Bucket 2 giây — cùng cách BE gộp "cùng một lỗi" giữa hai giám khảo (Phase doc §3). */
export const bucket2s = (t: number | null): number | null => (t === null ? null : Math.floor(t / 2));
export function findingView(f: ReviewFinding, criteria?: Criteria, stepKey?: StepReviewKey): FindingView {
  const shotOrder = shotOf(f.shotOrder);
  const unverified = !!f.unverified;
  const problem = f.problem ?? '';
  const frameNo = frameOf(f);
  const advisory = isAdvisoryOnlyCriterion(stepKey, f.criterion); // P6: V5 luôn gợi ý, kể cả khi BE gửi BLOCKING
  const timestampS = timeOf(f.timestampS);
  return {
    code: f.criterion, criterion: criterionText(f.criterion, criteria), blocking: f.severity === 'BLOCKING' && !unverified && !advisory,
    sevLabel: advisory ? 'Gợi ý' : f.severity === 'BLOCKING' ? 'Nặng' : 'Nhẹ', shotOrder, shotLabel: shotOrder ? `Shot ${shotOrder}` : isPr(stepKey) ? 'Cả prompt' : isCl(stepKey) ? 'Cả clip' : 'Cả video', // P5d: BE đặt shotOrder 0 = cả prompt
    evidence: cut(f.evidence ?? '', EVIDENCE_MAX), quote: (f.textEvidence ?? '').trim() || (f.evidence ?? '').trim(), problem, problems: problem ? [problem] : [], suggestion: f.suggestion ?? '', unverified,
    frameNo, frameLabel: frameLabel(frameNo), media: f.mediaEvidence ?? [], unverifiedReason: f.unverifiedReason ?? '',
    timestampS, timeLabel: timeLabelOf(timestampS),
  };
}
/** P5d: snapshot của phiếu prompt (BE gắn `step: 'PROMPT_REVIEW'`; snapshot phiếu hình không có `step`). */
export const isPromptSnapshot = (s: StepReview['snapshot']): s is PromptSnapshotMeta => !!s && (s as { step?: unknown }).step === 'PROMPT_REVIEW';
/** P6: snapshot của phiếu CLIP — BE gắn `step: 'CLIP_REVIEW'` (+ `frames[]`); snapshot hình không có `step`, snapshot prompt có `step: 'PROMPT_REVIEW'`. */
export const isClipSnapshot = (s: StepReview['snapshot']): s is ClipSnapshotMeta => {
  if (!s || typeof s !== 'object') return false;
  const o = s as unknown as Record<string, unknown>;
  return o.step === 'CLIP_REVIEW' && Array.isArray(o.frames);
};
/** Snapshot HÌNH (`media` / `frames`) của phiếu; phiếu prompt / clip / không có ⇒ null — không bao giờ đọc `media` từ snapshot chữ hay snapshot clip. */
export const mediaSnapshotOf = (s: StepReview['snapshot']): ReviewSnapshotMeta | null => (s && !isPromptSnapshot(s) && !isClipSnapshot(s) ? s : null);
const textOrNull = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);
/** P5d: gói câu AI trích + các bản chữ ĐÃ LƯU trong phiếu (finalPrompt / storedPrompt / reviewInput lúc chấm) để khung "Câu AI nhắc tới" tìm đúng bản giám khảo đã thấy. */
export function promptPickOf(quote: string, r: StepReview | null | undefined): PromptPick {
  const s = r && isPromptSnapshot(r.snapshot) ? r.snapshot : null;
  return { quote, finalPrompt: textOrNull(s?.prompt?.finalPrompt), storedPrompt: textOrNull(s?.prompt?.storedPrompt), reviewInput: textOrNull(s?.reviewInput) };
}
/** P5d: chỉ finding HỢP LỆ (câu trích đã được BE kiểm khớp nguyên văn) và có câu trích mới bấm được "xem trong prompt". */
export const canHighlightEvidence = (f: Pick<FindingView, 'unverified' | 'quote'>): boolean => !f.unverified && !!f.quote;
/** Duyệt B gom theo FRAME: "Cả sheet" (0) đứng đầu, rồi frame tăng dần; giữ thứ tự gốc trong từng nhóm. */
export function groupByFrame(fs: FindingView[]): Array<{ frameNo: number; label: string; findings: FindingView[] }> {
  const by = new Map<number, FindingView[]>();
  for (const f of fs) by.set(f.frameNo, [...(by.get(f.frameNo) ?? []), f]);
  return Array.from(by.entries()).sort((a, b) => a[0] - b[0]).map(([frameNo, findings]) => ({ frameNo, label: findings[0].frameLabel, findings }));
}
/** P6: AI chấm clip gom theo MỐC GIÂY tăng dần (finding không rõ mốc đứng cuối); giữ thứ tự gốc trong từng nhóm. */
export function groupByTime(fs: FindingView[]): Array<{ timestampS: number | null; label: string; findings: FindingView[] }> {
  const by = new Map<number | null, FindingView[]>();
  for (const f of fs) by.set(f.timestampS, [...(by.get(f.timestampS) ?? []), f]);
  const rank = (t: number | null): number => (t === null ? Number.POSITIVE_INFINITY : t);
  return Array.from(by.entries()).sort((a, b) => rank(a[0]) - rank(b[0])).map(([timestampS, findings]) => ({ timestampS, label: findings[0].timeLabel, findings }));
}
/** "Cả video" (0) đứng đầu, rồi shot tăng dần; giữ thứ tự gốc trong từng nhóm. */
export function groupByShot(fs: FindingView[]): Array<{ shotOrder: number; label: string; findings: FindingView[] }> {
  const by = new Map<number, FindingView[]>();
  for (const f of fs) by.set(f.shotOrder, [...(by.get(f.shotOrder) ?? []), f]);
  return Array.from(by.entries()).sort((a, b) => a[0] - b[0]).map(([shotOrder, findings]) => ({ shotOrder, label: findings[0].shotLabel, findings }));
}

/**
 * Lỗi CẢ HAI AI cùng thấy. BE trả `evidence` + `problems` [A, B]; gợi ý không có trong `agreed` nên lấy từ finding trùng (mã + shot) của giám khảo, không có ⇒ rỗng.
 * P5c (phiếu HÌNH, `stepKey = STORYBOARD_REVIEW`): `agreed[].shotOrder` là SỐ KHUNG (agreed không có `frameNo` riêng) ⇒ khớp theo (mã + KHUNG) — khác khung không phải cùng một lỗi;
 * bằng chứng hình lấy từ finding trùng của từng giám khảo (agreed không mang `mediaEvidence`).
 */
export function agreedView(r: StepReview, criteria?: Criteria): FindingView[] {
  const sbReview = isSb(r.stepKey);
  const clipReview = isCl(r.stepKey);
  return (r.agreed ?? []).map((a) => {
    const shot = shotOf(a.shotOrder);
    const frame = sbReview ? posInt(a.shotOrder) : 0;
    const at = timeOf(a.timestampS);
    // P6: lỗi chung của phiếu clip khớp finding theo (mã + bucket 2 giây) nếu BE nêu mốc; không nêu mốc ⇒ chỉ theo mã.
    const same = (f: ReviewFinding): boolean => f.criterion === a.criterion
      && (clipReview ? at === null || bucket2s(timeOf(f.timestampS)) === bucket2s(at) : sbReview ? frameOf(f) === frame : shotOf(f.shotOrder) === shot);
    const src = r.judges.flatMap((j) => j.findings ?? []).find((f) => same(f) && !!f.suggestion);
    const mediaEvidence = sbReview ? r.judges.flatMap((j) => (j.findings ?? []).find(same)?.mediaEvidence ?? []) : undefined;
    const base = findingView({
      criterion: a.criterion, severity: 'BLOCKING', shotOrder: shot, evidence: a.evidence ?? '', problem: '', suggestion: src?.suggestion ?? '', unverified: false,
      ...(sbReview ? { frameNo: frame } : {}), ...(mediaEvidence ? { mediaEvidence } : {}),
      ...(clipReview ? { timestampS: at ?? src?.timestampS, ...(src && typeof src.frameNo === 'number' ? { frameNo: src.frameNo } : {}) } : {}),
    }, criteria, r.stepKey);
    const problems = (a.problems ?? []).filter((p) => !!p);
    return { ...base, blocking: !isAdvisoryOnlyCriterion(r.stepKey, a.criterion), unverified: false, problems, problem: problems.join(' · ') };
  });
}

export interface JudgeColumn {
  label: string; model: string; failed: boolean; error: string; summary: string; findings: FindingView[]; blockingCount: number; passed: string[];
  /** P5c: dòng phủ `(tiêu chí × frame)` của giám khảo; phiếu P3d (không có coverage) ⇒ null. */
  coverage: string | null;
  /** P6: `AUDIO` = cột giám khảo nghe (V4) của phiếu clip; mọi cột khác `VISUAL`. */
  role: 'VISUAL' | 'AUDIO';
}
/**
 * Luôn đủ 2 cột (A · B). Nặng xếp trước nhẹ; cùng mức giữ thứ tự gốc.
 * P6: phiếu CLIP có thêm giám khảo NGHE (`role: 'AUDIO'`) ⇒ cột thứ ba "Giám khảo nghe"; A / B là các giám khảo HÌNH theo thứ tự trong `judges[]` (bước khác: không có `role` ⇒ tất cả là hình, hành vi cũ).
 */
export function judgeColumns(r: StepReview, criteria?: Criteria): JudgeColumn[] {
  const all = r.judges ?? [];
  const audio = all.find((j) => j.role === 'AUDIO');
  const visual = all.filter((j) => j.role !== 'AUDIO');
  const mk = (label: string, role: 'VISUAL' | 'AUDIO', j: StepReview['judges'][number] | undefined): JudgeColumn => {
    if (!j) return { label, role, model: '', failed: false, error: '', summary: '', findings: [], blockingCount: 0, passed: [], coverage: null };
    const vs = (j.findings ?? []).map((f) => findingView(f, criteria, r.stepKey));
    const findings = [...vs.filter((f) => f.sevLabel === 'Nặng'), ...vs.filter((f) => f.sevLabel !== 'Nặng')];
    return {
      label, role, model: j.model, failed: j.status === 'FAILED', error: j.error ?? '', summary: j.summary ?? '',
      findings, blockingCount: findings.filter((f) => f.blocking).length, passed: j.passed ?? [], coverage: coverageLine(coverageSummary(j)),
    };
  };
  const cols = [mk('Giám khảo A', 'VISUAL', visual[0]), mk('Giám khảo B', 'VISUAL', visual[1])];
  return audio ? [...cols, mk('Giám khảo nghe', 'AUDIO', audio)] : cols;
}

// ───────────────────────── lịch sử + neo shot ─────────────────────────

export interface HistoryRow { id: string; verdictLabel: string; decidedLabel: string | null; createdAt: string; cost: string }
/** BE `previous`: tối đa 5 phiếu DONE trước `latest`, mới → cũ. */
export function historyRows(item: StepReviewItem): HistoryRow[] {
  return (item.previous ?? []).slice(0, 5).map((h) => {
    const has = h.costUsd !== null && h.costUsd !== undefined && Number.isFinite(h.costUsd);
    return {
      id: h.id,
      verdictLabel: (VERDICT_CHIP[h.verdict] ?? VERDICT_CHIP.INCOMPLETE).label,
      decidedLabel: h.leaderDecision ? DECISION_LABEL[h.leaderDecision] : null,
      createdAt: h.createdAt,
      cost: !has ? '—' : `${h.costComplete ? '' : '~'}$${(h.costUsd as number).toFixed(4)}`,
    };
  });
}
/** Neo DOM của shot thứ N (vị trí 1-based) để "Shot N" cuộn tới. */
export const shotAnchorId = (position: number): string => `wf-shot-${position}`;
/**
 * `findings[].shotOrder = N` của BE là VỊ TRÍ 1-based sau khi sắp shot theo `orderIndex` rồi `id` (BE §6.13) — KHÔNG phải `orderIndex`.
 * Trả `shotId → vị trí` để đặt đúng neo kể cả khi `orderIndex` có lỗ hổng. Không đổi mảng gốc.
 */
export function shotPositions(shots: Array<{ id: string; orderIndex: number }>): Map<string, number> {
  const sorted = [...shots].sort((a, b) => a.orderIndex - b.orderIndex || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  return new Map(sorted.map((s, i) => [s.id, i + 1]));
}

// ───────────────────────── lỗi API ⇒ chữ ─────────────────────────

export type StepReviewErrorKind = 'CONFIG' | 'TOO_LARGE' | 'MEDIA' | 'BUDGET' | 'PRICE' | 'NOTHING' | 'STALE' | 'NOT_DONE' | 'NOTE' | 'BAD_REQUEST' | 'BUSY' | 'NOT_SKY' | 'NOT_FOUND' | 'OTHER';
export interface StepReviewError { kind: StepReviewErrorKind | string; text: string; reload: true }
const usd2 = (n: unknown): string | null => (typeof n === 'number' && Number.isFinite(n) ? `$${n.toFixed(2)}` : null);
/**
 * Lỗi của phiếu AI chấm (BE: `{ code, message, details? }`; `JOB_RUNNING` là phong bì busy `{ busy, code, jobType: 'FILM_STEP_REVIEW' }`).
 * Đứng TRƯỚC `workflowActionError`: các mã REVIEW_* / JUDGE_CONFIG_INVALID đều là 409 nên nhánh chung sẽ in "đang chạy ở nền" sai nghĩa;
 * `JOB_RUNNING` sẽ in tên việc thô; `NOT_SKY_PLAN` của nhánh chung nói về "máy tự chạy chặng". Mã khác (máy tự chạy, 404, lạ) rơi về nhánh chung.
 */
export function stepReviewActionError(err: any, what: string, stepKey?: StepReviewKey): StepReviewError {
  const sb = isSb(stepKey); // P5c: Duyệt B (ảnh) có chữ riêng ở các mã dưới; không truyền `stepKey` ⇒ chữ P3d y nguyên
  const pr = isPr(stepKey); // P5d: AI chấm prompt có chữ riêng ở REVIEW_INPUT_TOO_LARGE / NOTHING_TO_REVIEW / BUDGET_EXCEEDED
  const cl = isCl(stepKey); // P6: AI chấm clip có chữ riêng ở JUDGE_CONFIG_INVALID / TOO_LARGE / MEDIA_UNAVAILABLE / BUDGET / PRICE / NOTHING / STALE
  const d = err?.response?.data;
  const code = typeof d?.code === 'string' ? d.code : '';
  const mk = (kind: StepReviewErrorKind, text: string): StepReviewError => ({ kind, text: `${what}: ${text}`, reload: true });
  if (code === 'JUDGE_CONFIG_INVALID') {
    if (cl) return mk('CONFIG', 'chưa chọn được đủ 2 giám khảo AI xem được hình, khác hãng với nhau và khác hãng với model tạo video, nên chưa gọi AI (chưa tốn tiền) — báo người quản trị chỉnh cấu hình model.');
    return mk('CONFIG', sb
      ? 'chưa chọn được đủ 2 giám khảo AI xem được ảnh, khác hãng với model vẽ ảnh storyboard, nên chưa gọi AI (chưa tốn tiền) — báo người quản trị chỉnh cấu hình model.'
      : 'chưa chọn được đủ 2 giám khảo AI khác hãng với model viết nên chưa gọi AI (chưa tốn tiền) — báo người quản trị chỉnh cấu hình model.');
  }
  if (code === 'REVIEW_INPUT_TOO_LARGE') {
    if (pr) return mk('TOO_LARGE', 'prompt hoặc danh sách ảnh đính quá dài để đưa cho giám khảo nên chưa gọi AI — rút gọn prompt / bớt ảnh đính rồi chấm lại.');
    if (cl) return mk('TOO_LARGE', 'chữ hoặc số khung hình của clip đưa cho giám khảo vượt giới hạn nên chưa gọi AI — báo người quản trị (không tự cắt bớt khung hình).');
    return mk('TOO_LARGE', sb
      ? 'chữ hoặc số ảnh đưa cho giám khảo vượt giới hạn nên chưa gọi AI — báo người quản trị (không tự cắt bớt ảnh).'
      : 'dữ liệu của video quá dài để đưa cho giám khảo nên chưa gọi AI — rút bớt shot / thoại rồi chấm lại.');
  }
  if (code === 'REVIEW_MEDIA_UNAVAILABLE' && cl) return mk('MEDIA', 'không trích được khung hình hoặc âm thanh của clip (file hỏng, mất hoặc không đọc được) nên chưa gọi AI (chưa tốn tiền) — thử lại sau, vẫn lỗi thì báo người quản trị.');
  if (code === 'REVIEW_MEDIA_UNAVAILABLE') return mk('MEDIA', 'không đọc được ảnh cần đưa cho giám khảo (ảnh hỏng, mất hoặc không thuộc nguồn cho phép) nên chưa gọi AI (chưa tốn tiền) — tải lại rồi thử lại, vẫn lỗi thì báo người quản trị.');
  if (code === 'REVIEW_BUDGET_EXCEEDED') {
    const used = usd2(d?.details?.usedUsd); const cap = usd2(d?.details?.capUsd);
    const nums = used && cap ? ` (đã dùng / giữ chỗ ${used}, trần ${cap})` : '';
    return mk('BUDGET', `đã chạm trần tiền chấm ${sb ? 'ảnh storyboard của plan này' : pr ? 'prompt của plan này' : cl ? 'clip của plan này' : 'của tập này'}${nums} nên chưa gọi AI — báo người quản trị nếu cần nâng trần.`);
  }
  if (code === 'REVIEW_PRICE_UNKNOWN') {
    if (cl) return mk('PRICE', 'chưa có bảng giá / cách ước tiền khung hình + âm thanh cho một giám khảo nên không ước được tiền và chưa gọi AI — báo người quản trị bổ sung cấu hình giá.');
    return mk('PRICE', sb
      ? 'chưa có bảng giá / cách ước tiền ảnh cho một giám khảo nên không ước được tiền và chưa gọi AI — báo người quản trị bổ sung cấu hình giá.'
      : 'chưa biết giá của một giám khảo nên không ước được tiền và chưa gọi AI — thử lại sau ít phút.');
  }
  if (code === 'REVIEW_NOTHING_TO_REVIEW') {
    if (pr) return mk('NOTHING', 'video này chưa có prompt (hoặc chưa dựng được bản gửi engine) để chấm — dựng prompt trước.');
    if (cl) return mk('NOTHING', 'bản clip này chưa tạo xong (hoặc chưa có file video) để chấm — đợi clip tạo xong rồi chấm.');
    return mk('NOTHING', sb
      ? 'video này chưa có ảnh storyboard để chấm — dựng ảnh storyboard trước.'
      : 'bước này chưa có kết quả để chấm (chưa có shot, hoặc Quay phim chưa chạy) — chạy bước đó trước.');
  }
  if (code === 'REVIEW_STALE' && cl) return mk('STALE', 'clip của bản này đã đổi (video, độ dài hoặc khung hình) nên phiếu không còn khớp — đã tải lại, bấm Chấm lại rồi mới phân xử.');
  if (code === 'REVIEW_STALE' && pr) return mk('STALE', 'prompt hoặc ảnh đính của video đã đổi nên phiếu không còn khớp — đã tải lại, bấm Chấm lại rồi mới phân xử.');
  if (code === 'REVIEW_STALE') return mk('STALE', 'phiếu đã cũ vì dữ liệu của bước đã đổi sau lúc chấm — đã tải lại, bấm Chấm lại rồi mới phân xử.');
  if (code === 'REVIEW_NOT_DONE') return mk('NOT_DONE', 'phiếu này chưa xong nên chưa phân xử được — đợi chấm xong rồi bấm Tải lại.');
  if (code === 'REVIEW_NOTE_REQUIRED') return mk('NOTE', 'phiếu có lỗi nên phải ghi chú lý do trước khi phân xử.');
  if (code === 'CLIP_ID_REQUIRED') return mk('BAD_REQUEST', 'chưa biết chấm bản clip nào — hãy chọn bản clip rồi bấm lại (tải lại trang nếu vẫn lỗi).');
  if (code === 'NOT_FOUND' && cl) return mk('NOT_FOUND', 'bản clip này không còn hoặc không thuộc video này (có thể đã bị xoá) — đã tải lại danh sách bản clip.');
  if (code === 'BAD_DECISION' || code === 'BAD_STEP') return mk('BAD_REQUEST', 'yêu cầu không hợp lệ (lựa chọn phân xử hoặc bước chấm không đúng) — tải lại trang rồi thử lại.');
  if (code === 'NOT_SKY_PLAN') return mk('NOT_SKY', 'plan này không dùng luồng mới — phiếu AI chấm chỉ dùng cho plan dùng luồng mới.');
  const reviewJob = d?.jobType === REVIEW_JOB_TYPE || String(d?.details?.jobKey ?? '').startsWith('review:') || (!d?.jobType && !d?.details?.jobKey);
  if (code === 'JOB_RUNNING' && reviewJob) return mk('BUSY', cl ? 'bản clip này đang được chấm ở nền — đợi xong rồi bấm Tải lại, đừng bấm lại.' : 'bước này đang được chấm ở nền — đợi xong rồi bấm Tải lại, đừng bấm lại.');
  const base = workflowActionError(err, what);
  return { kind: base.kind, text: base.text, reload: true };
}
